import { format, parseISO } from "date-fns";
import { formatCurrency } from "../../../../lib/utils/utils-functions";
import { replacePdfPlaceholders } from "../../../../pdf-templates/utils";
import {
	PaymentReminderDocument,
	PaymentReminderRecipient,
} from "../payment-reminder.types";

export const REMINDER_PLACEHOLDERS = [
	{ token: "#ANREDE", label: "Anrede" },
	{ token: "#RGNR", label: "Rechnungsnummer" },
	{ token: "#RGDATUM", label: "Rechnungsdatum" },
	{ token: "#RGBETRAG", label: "Rechnungsbetrag" },
	{ token: "#BEZAHLT", label: "Bereits bezahlt" },
	{ token: "#OFFEN", label: "Offener Betrag" },
	{ token: "#GEBUEHR", label: "Mahngebühren" },
	{ token: "#TOTAL", label: "Total inkl. Gebühren" },
	{ token: "#FRIST", label: "Zahlungsfrist" },
	{ token: "#STUFE", label: "Mahnstufe" },
] as const;

const FORMAL_GREETING = "Sehr geehrte Damen und Herren,";

export function getReminderSalutation(
	recipient: PaymentReminderRecipient
): string {
	const useCorrespondence = recipient.is_correspondence_address === true;
	const isCompany = useCorrespondence
		? recipient.c_is_company
		: recipient.is_company;
	if (isCompany) return FORMAL_GREETING;

	const name = (
		useCorrespondence
			? [recipient.c_title, recipient.c_first_name, recipient.c_last_name]
			: [recipient.title, recipient.first_name, recipient.last_name]
	)
		.map((part) => part?.trim())
		.filter(Boolean)
		.join(" ");

	const hasName = useCorrespondence
		? recipient.c_first_name?.trim() || recipient.c_last_name?.trim()
		: recipient.first_name?.trim() || recipient.last_name?.trim();

	return hasName ? `Guten Tag ${name},` : FORMAL_GREETING;
}

export function getOpenReminderFees(document: PaymentReminderDocument): number {
	return document.fees.reduce((sum, fee) => sum + fee.open_amount, 0);
}

export function formatReminderDate(isoDate: string): string {
	return format(parseISO(isoDate), "dd.MM.yyyy");
}

export function resolveReminderPlaceholders(
	text: string,
	document: PaymentReminderDocument
): string {
	const { invoice, reminder, recipient } = document;
	const currency = invoice.currency;
	const openFees = getOpenReminderFees(document);

	return replacePdfPlaceholders(text, {
		"#ANREDE": getReminderSalutation(recipient),
		"#RGNR": invoice.doc_number,
		"#RGDATUM": formatReminderDate(invoice.date),
		"#RGBETRAG": formatCurrency(invoice.value, currency),
		"#BEZAHLT": formatCurrency(invoice.paid_amount, currency),
		"#OFFEN": formatCurrency(invoice.open_amount, currency),
		"#GEBUEHR": formatCurrency(openFees, currency),
		"#TOTAL": formatCurrency(invoice.open_amount + openFees, currency),
		"#FRIST": formatReminderDate(reminder.deadline),
		"#STUFE": String(reminder.stage),
	});
}
